import { useAuth } from '@berky/auth'
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  LoadingSkeleton,
  PageHeader,
  Tabs,
  TabsContent,
  ResponsiveTabsList,
  TabsTrigger,
} from '@berky/ui'
import { ChevronDown, Info, UserPlus } from 'lucide-react'
import { Navigate } from 'react-router'
import { useState } from 'react'

import { PLATFORM_ADMIN_ROLE } from './api'
import { roleAccess } from '../module-access'
import { GruppenTab } from './gruppen-tab'
import { PersonenTab } from './personen-tab'
import { RollenTab } from './rollen-tab'
import { RechteVergabeDialog } from './rechte-vergabe-dialog'
import { AuditLogTab } from './audit-log-tab'

/**
 * Die Verwaltung von Personen, Gruppen und Rollen.
 *
 * Bewusst Teil der Shell und kein Modul: Sie verwaltet die Rollen aller Module
 * und dürfte als Modul aus keinem von ihnen etwas wissen.
 */
export function VerwaltungPage() {
  const { user, isLoading } = useAuth()
  const access = roleAccess(user?.roles, [PLATFORM_ADMIN_ROLE])
  const [rechteVergabeOffen, setRechteVergabeOffen] = useState(false)
  const [erklaerungOffen, setErklaerungOffen] = useState(false)

  if (isLoading || access === 'unbekannt') {
    return <LoadingSkeleton variant="text" rows={4} />
  }

  if (access !== 'erlaubt') {
    return <Navigate to="/forbidden" replace state={{ moduleTitle: 'Verwaltung' }} />
  }

  return (
    <>
      <PageHeader
        title="Nutzer und Rollen"
        description="Wer darf was — übersichtlich vergeben über Personen, Gruppen und den Rollenkatalog."
        actions={
          <Button onClick={() => setRechteVergabeOffen(true)}>
            <UserPlus aria-hidden="true" />
            Rechte vergeben
          </Button>
        }
      />

      <div className="space-y-4">
        <section className="rounded-lg border bg-card/70 shadow-sm">
          <Button
            variant="ghost"
            className="h-auto w-full justify-between gap-4 rounded-lg px-4 py-3 text-left hover:bg-muted/50"
            aria-expanded={erklaerungOffen}
            aria-controls="berechtigungen-erklaerung"
            onClick={() => setErklaerungOffen((offen) => !offen)}
          >
            <span className="flex min-w-0 items-center gap-3">
              <Info className="size-4 shrink-0 text-primary" aria-hidden="true" />
              <span className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-0.5">
                <span className="font-medium">Wie funktionieren Berechtigungen?</span>
                <span className="text-sm font-normal text-muted-foreground">
                  Recht → Rolle → Person oder Gruppe
                </span>
              </span>
            </span>
            <ChevronDown
              className={`size-4 shrink-0 transition-transform ${erklaerungOffen ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </Button>

          {erklaerungOffen && (
            <div id="berechtigungen-erklaerung" className="border-t px-4 py-4">
              <Alert className="border-0 bg-transparent p-0 shadow-none">
                <AlertTitle>So funktionieren Berechtigungen</AlertTitle>
                <AlertDescription>
                  <div className="space-y-4">
                    <p>
                      Ein <strong>Recht</strong> beschreibt, was jemand in einem Modul tun
                      darf. Mehrere Rechte werden zu einer verständlichen{' '}
                      <strong>Rolle</strong> gebündelt. Diese Rolle vergeben Sie direkt an
                      eine <strong>Person</strong> oder an eine <strong>Gruppe</strong>.
                    </p>

                    <div className="grid gap-3 sm:grid-cols-3">
                      <div className="rounded-md border bg-card/60 p-3">
                        <p className="font-medium text-foreground">Recht</p>
                        <p className="mt-1 text-xs">
                          Einzelne Erlaubnis innerhalb eines Moduls.
                        </p>
                      </div>
                      <div className="rounded-md border bg-card/60 p-3">
                        <p className="font-medium text-foreground">Rolle</p>
                        <p className="mt-1 text-xs">
                          Bündelt mehrere Rechte, zum Beispiel Bearbeiten.
                        </p>
                      </div>
                      <div className="rounded-md border bg-card/60 p-3">
                        <p className="font-medium text-foreground">Person oder Gruppe</p>
                        <p className="mt-1 text-xs">
                          Erhält die Rolle direkt oder über eine Gruppe.
                        </p>
                      </div>
                    </div>

                    <div className="rounded-md border bg-card/60 p-3 text-sm">
                      <p className="font-medium text-foreground">
                        Aktueller Stand mit Entra
                      </p>
                      <p className="mt-1">
                        Berky Digital steuert, welche Rolle eine Person oder Entra-Gruppe
                        erhält. Entra liefert die Accounts und derzeit auch die
                        Gruppenmitgliedschaften. Deshalb verwalten Sie Mitglieder einer
                        Entra-Gruppe gegebenenfalls weiterhin in Microsoft 365 / Entra.
                      </p>
                    </div>

                    <div className="rounded-md border border-dashed bg-muted/40 p-3 text-sm">
                      <p className="font-medium text-foreground">
                        Für die Zukunft vorgemerkt
                      </p>
                      <p className="mt-1">
                        Wir können später entscheiden, ob Gruppenmitgliedschaften
                        weiterhin in Entra gepflegt werden oder ob Berky Digital eigene
                        Gruppen mit eigener Mitgliederverwaltung bekommt. Die Rollen- und
                        Rechtevergabe bleibt dabei in Berky Digital nachvollziehbar.
                      </p>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
            </div>
          )}
        </section>

        <Tabs defaultValue="personen">
          <ResponsiveTabsList>
            <TabsTrigger value="personen">Personen</TabsTrigger>
            <TabsTrigger value="gruppen">Gruppen</TabsTrigger>
            <TabsTrigger value="rollen">Rollen</TabsTrigger>
            <TabsTrigger value="protokoll">Änderungsprotokoll</TabsTrigger>
          </ResponsiveTabsList>

          <TabsContent value="personen">
            <PersonenTab />
          </TabsContent>

          <TabsContent value="gruppen">
            <GruppenTab />
          </TabsContent>

          <TabsContent value="rollen">
            <RollenTab />
          </TabsContent>

          <TabsContent value="protokoll">
            <AuditLogTab />
          </TabsContent>
        </Tabs>
      </div>

      {rechteVergabeOffen && (
        <RechteVergabeDialog
          open
          onClose={() => setRechteVergabeOffen(false)}
          onDone={async () => undefined}
        />
      )}
    </>
  )
}
